import type { SupplyPlanVariant } from "~/context/SupplyPlanProvider"
import type { FashionSkuRow } from "~/hooks/useFashionSkuRows"
import { formatVariantId } from "~/utils/helpers"
import {
  BUY_GROUP_BY_VALUES,
  BUY_GROUP_OPTIONS,
  ALPHA_GROUP_SORTING,
  getBuyGroupSorting,
  type GroupSorting,
  type GroupOption,
} from "~/utils/productTableUtils"

export const FASHION_GROUP_BY_VALUES = [...BUY_GROUP_BY_VALUES, "fabric"] as const
export type FashionGroupBy = (typeof FASHION_GROUP_BY_VALUES)[number]

export const FASHION_GROUP_OPTIONS: GroupOption<FashionGroupBy>[] = [
  ...BUY_GROUP_OPTIONS,
  { value: "fabric", label: "Fabric", productLike: false },
]

export const getFashionGroupSorting = (groupBy: FashionGroupBy): GroupSorting =>
  groupBy === "fabric" ? ALPHA_GROUP_SORTING : getBuyGroupSorting(groupBy)

/** Group key for a row with no value in the grouped dimension. */
export const NO_GROUP_VALUE = "—"

/** Only the fields grouping actually reads. Structural on purpose. */
export type FashionGroupRow = Pick<
  FashionSkuRow,
  "variantId" | "productName" | "size" | "sizeDimension1" | "sizeDimension2"
> & {
  variant: Pick<SupplyPlanVariant, "color" | "groupVendor" | "fabric">
}

export function getFashionGroupKey(row: FashionGroupRow, groupBy: FashionGroupBy): string {
  switch (groupBy) {
    case "product":
      return row.productName ?? NO_GROUP_VALUE
    case "color":
      return row.variant.color ?? NO_GROUP_VALUE
    case "size":
      return row.size ?? NO_GROUP_VALUE
    case "sizeDimension1":
      return row.sizeDimension1 ?? NO_GROUP_VALUE
    case "sizeDimension2":
      return row.sizeDimension2 ?? NO_GROUP_VALUE
    case "vendor":
      return row.variant.groupVendor ?? NO_GROUP_VALUE
    case "fabric":
      return row.variant.fabric ?? NO_GROUP_VALUE
    case "variant":
    default:
      return row.variantId ? formatVariantId(row.variantId) : ""
  }
}

// Only offer dimensions that have data on these rows. Variant/Product are always present.
export function getAvailableFashionGroupOptions(
  rows: FashionGroupRow[],
  sizeDimensionsEnabled = false,
): typeof FASHION_GROUP_OPTIONS {
  return FASHION_GROUP_OPTIONS.filter((o) => {
    switch (o.value) {
      case "color":
        return rows.some((r) => !!r.variant.color)
      case "size":
        return rows.some((r) => !!r.size)
      case "sizeDimension1":
        return sizeDimensionsEnabled && rows.some((r) => !!r.sizeDimension1)
      case "sizeDimension2":
        return sizeDimensionsEnabled && rows.some((r) => !!r.sizeDimension2)
      case "vendor":
        return rows.some((r) => !!r.variant.groupVendor)
      case "fabric":
        return rows.some((r) => !!r.variant.fabric)
      default:
        return true
    }
  })
}

export function resolveEffectiveFashionGroupBy(
  groupBy: FashionGroupBy,
  available: { value: FashionGroupBy }[],
): FashionGroupBy {
  return available.some((o) => o.value === groupBy) ? groupBy : "variant"
}
